import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import IndustrialProduct from "./components/IndustrialProduct";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";

import "./GasInstallation.css";

/* ============================================================
   API
============================================================ */

const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* ============================================================
   OBTENER CATÁLOGO
============================================================ */

const obtenerCatalogoGas = async () => {
  const respuesta = await axios.get(API_URL, {
    timeout: 45000,
  });

  return respuesta.data || {};
};

/* ============================================================
   COMPONENTE
============================================================ */

function GasInstallation() {
  /* ============================================================
     FILTROS
  ============================================================ */

  const [filtroSeleccionado, setFiltroSeleccionado] = useState("todos");

  const [filtroBusqueda, setFiltroBusqueda] = useState("");

  /* ============================================================
     PAGINACIÓN
  ============================================================ */

  const [paginaActual, setPaginaActual] = useState(1);

  const productosPorPagina = 16;

  /* ============================================================
     BÚSQUEDA DESDE NAVBAR
  ============================================================ */

  const location = useLocation();

  const params = new URLSearchParams(location.search);

  const busquedaDesdeNavbar =
    params.get("search") || params.get("busqueda") || "";

  /* ============================================================
     TANSTACK QUERY + INDEXEDDB
  ============================================================ */

  const {
    data: catalogo = {},
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["catalogo-gas"],
    queryFn: obtenerCatalogoGas,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (isError) {
    console.error("Error al obtener productos de gas:", error);
  }

  /* ============================================================
     BÚSQUEDA DESDE NAVBAR
  ============================================================ */

  useEffect(() => {
    if (busquedaDesdeNavbar) {
      setFiltroBusqueda(busquedaDesdeNavbar);
      setFiltroSeleccionado("todos");
      setPaginaActual(1);
    }
  }, [busquedaDesdeNavbar]);

  /* ============================================================
     SCROLL PAGINACIÓN
  ============================================================ */

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [paginaActual]);

  /* ============================================================
     NORMALIZAR TEXTO
  ============================================================ */

  const normalizarTexto = (texto = "") => {
    return String(texto)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  };

  /* ============================================================
     BÚSQUEDA
  ============================================================ */

  const coincideBusqueda = (item) => {
    if (!filtroBusqueda.trim()) {
      return true;
    }

    const palabras = normalizarTexto(filtroBusqueda)
      .split(/\s+/)
      .filter(Boolean);

    const textoProducto = normalizarTexto(
      `${item?.nombre || ""} ${item?.marca || ""} ${
        item?.modelo || ""
      } ${item?.codigoProducto || ""} ${item?.descripcion || ""}`,
    );

    return palabras.every((palabra) => textoProducto.includes(palabra));
  };

  /* ============================================================
     CATEGORÍAS
  ============================================================ */

  const categorias = useMemo(
    () => ({
      accesorio: catalogo.accesorios || [],
      centralina: catalogo.centralinas || [],
      regulador: catalogo.reguladores || [],
      manguera: catalogo.manguerasGLP || [],
    }),
    [catalogo],
  );

  /* ============================================================
     TODOS LOS PRODUCTOS
  ============================================================ */

  const todosLosProductos = useMemo(() => {
    return Object.values(categorias).flat();
  }, [categorias]);

  /* ============================================================
     PRODUCTOS FILTRADOS
  ============================================================ */

  const productosFiltrados = useMemo(() => {
    const resultado = {};

    Object.entries(categorias).forEach(([categoria, productos]) => {
      resultado[categoria] = productos.filter(coincideBusqueda);
    });

    resultado.todos = todosLosProductos.filter(coincideBusqueda);

    return resultado;
  }, [categorias, todosLosProductos, filtroBusqueda]);

  /* ============================================================
     NOMBRES / TÍTULOS / ICONOS
  ============================================================ */

  const nombresLegibles = {
    todos: "Todos los productos",
    accesorio: "Accesorios",
    centralina: "Centralinas",
    regulador: "Reguladores de Alta Presión",
    manguera: "Mangueras",
  };

  const titulosCategoria = {
    todos: "Componentes para Instalaciones a Gas",
    accesorio: "Accesorios para Instalaciones a Gas",
    centralina: "Centralinas",
    regulador: "Reguladores de Alta Presión",
    manguera: "Mangueras para Instalaciones a Gas",
  };

  const iconosCategoria = {
    todos: "bi-grid",
    accesorio: "bi-tools",
    centralina: "bi-diagram-3-fill",
    regulador: "bi-speedometer2",
    manguera: "bi-bezier2",
  };

  const obtenerTituloCategoria = () =>
    titulosCategoria[filtroSeleccionado] || titulosCategoria.todos;

  /* ============================================================
     PRODUCTOS / PAGINACIÓN
  ============================================================ */

  const productosCategoria = productosFiltrados[filtroSeleccionado] || [];

  const totalProductos = todosLosProductos.length;

  const productosMostrados = productosCategoria.length;

  const productosEnPagina =
    filtroSeleccionado === "todos"
      ? productosCategoria.slice(
          (paginaActual - 1) * productosPorPagina,
          paginaActual * productosPorPagina,
        )
      : productosCategoria;

  const totalPaginas =
    filtroSeleccionado === "todos"
      ? Math.ceil(productosCategoria.length / productosPorPagina)
      : 1;

  /* ============================================================
     SELECCIONAR CATEGORÍA
  ============================================================ */

  const seleccionarCategoria = (categoria) => {
    setFiltroSeleccionado(categoria);
    setFiltroBusqueda("");
    setPaginaActual(1);
  };

  /* ============================================================
     CAMBIAR PÁGINA
  ============================================================ */

  const cambiarPagina = (pagina) => {
    if (pagina < 1 || pagina > totalPaginas) {
      return;
    }

    setPaginaActual(pagina);
  };

  /* ============================================================
     VISTA
  ============================================================ */

  return (
    <div className="gas-page">
      <Navbar />

      <main>
        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="gas-hero">
          <div className="gas-container">
            <div className="gas-hero-card">
              <div>
                <span className="gas-kicker">CATÁLOGO G&amp;J FRÍO</span>

                <h1>{obtenerTituloCategoria()}</h1>

                <p>
                  Encuentra accesorios, centralinas, reguladores y componentes
                  para instalaciones de gas.
                </p>
              </div>

              <div className="gas-hero-icon">
                <i className="bi bi-fire"></i>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            TOOLBAR
        ===================================================== */}

        <section className="gas-toolbar-section">
          <div className="gas-container">
            <div className="gas-toolbar">
              <div className="gas-toolbar-left">
                <button
                  type="button"
                  className="gas-category-button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#gasCategories"
                  aria-controls="gasCategories"
                >
                  <i className="bi bi-grid-fill"></i>

                  <span>Categorías</span>

                  <i className="bi bi-chevron-right"></i>
                </button>

                <div className="gas-current-category">
                  <span>Categoría actual</span>

                  <strong>{nombresLegibles[filtroSeleccionado]}</strong>
                </div>
              </div>

              <div className="gas-counter">
                <span>Mostrando</span>

                <strong>{!isLoading ? productosMostrados : 0}</strong>

                <span>de</span>

                <strong>{!isLoading ? totalProductos : 0}</strong>

                <span>{totalProductos === 1 ? "artículo" : "artículos"}</span>
              </div>
            </div>

            {/* =================================================
                BUSCADOR
            ================================================= */}

            <div className="gas-search-row">
              <div className="gas-search">
                <i className="bi bi-search"></i>

                <input
                  type="text"
                  placeholder="Buscar por nombre, marca, modelo o código..."
                  value={filtroBusqueda}
                  onChange={(e) => {
                    setFiltroBusqueda(e.target.value);
                    setPaginaActual(1);
                  }}
                />

                {filtroBusqueda && (
                  <button
                    type="button"
                    className="gas-search-clear"
                    onClick={() => {
                      setFiltroBusqueda("");
                      setPaginaActual(1);
                    }}
                    aria-label="Limpiar búsqueda"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            OFFCANVAS
        ===================================================== */}

        <div
          className="offcanvas offcanvas-start gas-offcanvas"
          data-bs-scroll="true"
          tabIndex="-1"
          id="gasCategories"
          aria-labelledby="gasCategoriesLabel"
        >
          <div className="offcanvas-header gas-offcanvas-header">
            <div>
              <span>CATÁLOGO</span>

              <h5 className="offcanvas-title" id="gasCategoriesLabel">
                Subcategorías
              </h5>
            </div>

            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="offcanvas"
              aria-label="Cerrar"
            ></button>
          </div>

          <div className="offcanvas-body gas-offcanvas-body">
            <div className="gas-category-list">
              {Object.keys(nombresLegibles).map((categoria) => {
                const cantidad = productosFiltrados[categoria]?.length || 0;

                return (
                  <button
                    type="button"
                    key={categoria}
                    className={`gas-category-item ${
                      filtroSeleccionado === categoria ? "active" : ""
                    }`}
                    onClick={() => seleccionarCategoria(categoria)}
                    data-bs-dismiss="offcanvas"
                  >
                    <span className="gas-category-item-icon">
                      <i className={`bi ${iconosCategoria[categoria]}`}></i>
                    </span>

                    <span className="gas-category-item-text">
                      <strong>{nombresLegibles[categoria]}</strong>

                      <small>
                        {!isLoading ? cantidad : 0}{" "}
                        {cantidad === 1 ? "producto" : "productos"}
                      </small>
                    </span>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* =====================================================
            PRODUCTOS
        ===================================================== */}

        <section className="gas-products-section">
          <div className="gas-container">
            {/* =================================================
                SPINNER
            ================================================= */}

            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  minHeight: "100px",
                }}
              >
                <div
                  className="spinner-grow text-primary"
                  role="status"
                  style={{
                    width: "1.6rem",
                    height: "1.6rem",
                  }}
                >
                  <span className="visually-hidden">Cargando productos...</span>
                </div>
              </div>
            ) : productosEnPagina.length > 0 ? (
              /* ===============================================
                 PRODUCTOS
              =============================================== */

              <div className="gas-products-grid">
                {productosEnPagina.map((item) => (
                  <IndustrialProduct
                    key={item._id}
                    img={item.imagen}
                    title={item.nombre}
                    id={item._id}
                    details={[
                      ...(item.marca
                        ? [
                            {
                              label: "Marca",
                              value: item.marca,
                            },
                          ]
                        : []),

                      ...(item.modelo
                        ? [
                            {
                              label: "Modelo",
                              value: item.modelo,
                            },
                          ]
                        : []),

                      {
                        label: "Código Producto",
                        value: item.codigoProducto,
                      },

                      {
                        label: "Descripción",
                        value: item.descripcion,
                      },
                    ]}
                  />
                ))}
              </div>
            ) : (
              /* ===============================================
                 SIN RESULTADOS
              =============================================== */

              <div className="gas-empty">
                <div className="gas-empty-icon">
                  <i className="bi bi-search"></i>
                </div>

                <h3>No encontramos productos</h3>

                <p>
                  No existen coincidencias para la búsqueda o categoría
                  seleccionada.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setFiltroBusqueda("");
                    setFiltroSeleccionado("todos");
                    setPaginaActual(1);
                  }}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                  Mostrar todos
                </button>
              </div>
            )}

            {/* =================================================
                PAGINACIÓN
            ================================================= */}

            {!isLoading &&
              filtroSeleccionado === "todos" &&
              totalPaginas > 1 && (
                <nav
                  className="gas-pagination-wrapper"
                  aria-label="Paginación de productos"
                >
                  <button
                    type="button"
                    className="gas-pagination-arrow"
                    disabled={paginaActual === 1}
                    onClick={() => cambiarPagina(paginaActual - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>

                    <span>Anterior</span>
                  </button>

                  <div className="gas-pagination-pages">
                    {Array.from(
                      {
                        length: totalPaginas,
                      },
                      (_, index) => index + 1,
                    ).map((pagina) => (
                      <button
                        type="button"
                        key={pagina}
                        className={`gas-page-button ${
                          paginaActual === pagina ? "active" : ""
                        }`}
                        onClick={() => cambiarPagina(pagina)}
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="gas-pagination-arrow"
                    disabled={paginaActual === totalPaginas}
                    onClick={() => cambiarPagina(paginaActual + 1)}
                  >
                    <span>Siguiente</span>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                </nav>
              )}
          </div>
        </section>
      </main>

      <Footer />

      <WhatsAppButton />
    </div>
  );
}

export default GasInstallation;
